import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: 'USER' | 'ADMIN' | 'MASTER' | 'SUPER_ADMIN';
  fallbackPath?: string;
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackPath = '/app/dashboard'
}) => {
  const { user, loading } = useAuth();

  // Show loading spinner while auth is being verified
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, this should not happen as ProtectedRoute should handle it
  // But adding as safety measure
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role hierarchy
  const hasRequiredRole = () => {
    const roleHierarchy = {
      'USER': 0,
      'ADMIN': 1,
      'MASTER': 2,
      'SUPER_ADMIN': 3
    };

    const userLevel = roleHierarchy[user.role];
    const requiredLevel = roleHierarchy[requiredRole];

    return userLevel >= requiredLevel;
  };

  // If user doesn't have required role, redirect to fallback
  if (!hasRequiredRole()) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;